import React, { useState } from 'react';
import { getAvatarColor } from '../utils/colors';
import type { TeacherListItem } from '../utils/types';
import SlideToSubmit from './SlideToSubmit';
import { markTeacherAttendance } from '../Api/auth';

interface Props {
  teacher: TeacherListItem;
  photo: string;
  captureTime: Date;
  onRetake: () => void;
  onSubmit: (updatedTeacher: TeacherListItem) => void;
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function formatDate(d: Date) {
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(d: Date) {
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toUpperCase();
}

function timeStr(d: Date) {
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
}

const PhotoPreview: React.FC<Props> = ({ teacher, photo, captureTime, onRetake, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError]     = useState('');

  const handleConfirm = async () => {
    setSubmitting(true);
    setApiError('');
    let status: 'present' | 'absent' | 'late' = 'present';
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 10000)
      );
      const record = await Promise.race([
        markTeacherAttendance(teacher.id, 'present', photo),
        timeoutPromise,
      ]);
      status = record.status;
    } catch {
      // Proceed even if API fails or times out — record locally
    }
    const updated: TeacherListItem = { ...teacher, todayStatus: status, markedAt: timeStr(captureTime) };
    onSubmit(updated);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#fff',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif", overflow: 'hidden',
    }}>
      {/* Top bar */}
      <div style={{
        position: 'relative', zIndex: 3,
        paddingTop: 'env(safe-area-inset-top, 16px)',
        padding: 'env(safe-area-inset-top, 16px) 20px 0',
      }}>
        <div style={{ paddingTop: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          {!submitting && (
            <button onClick={onRetake} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#f1f5f9',
              border: '1px solid #e2e8f0', borderRadius: 20, padding: '8px 16px 8px 10px',
              cursor: 'pointer', color: '#1e293b', fontSize: 14, fontWeight: 600,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
              </svg>
              Retake
            </button>
          )}
          <div style={{ flex: 1 }} />
          <div style={{
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: 12, padding: '6px 12px',
            fontSize: 12, fontWeight: 600, color: '#64748b',
          }}>
            Preview
          </div>
        </div>
      </div>

      {/* Middle: passport photo centered in remaining space */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px 20px',
      }}>
        <div style={{
          width: '80vw', aspectRatio: '35 / 45',
          borderRadius: '2vw',
          border: '3px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <img
            src={photo} alt="Captured"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      </div>

      {/* Bottom content */}
      <div style={{
        position: 'relative', zIndex: 3,
        padding: '0 20px 0',
        paddingBottom: 'max(env(safe-area-inset-bottom), 28px)',
      }}>
        {/* Teacher info + timestamp */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: getAvatarColor(teacher.name),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 800, color: '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
          }}>
            {initials(teacher.name)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{teacher.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {teacher.role === 'class_teacher' ? 'Class Teacher' : 'Subject Teacher'} · {teacher.subject}
            </div>
          </div>
        </div>

        {/* Date/Time card */}
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 16, padding: '14px 18px', marginBottom: 18,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{formatDate(captureTime)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{formatTime(captureTime)}</span>
            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>Photo captured</span>
          </div>
        </div>

        {/* API error */}
        {apiError && (
          <div style={{ fontSize: 13, color: '#dc2626', textAlign: 'center', marginBottom: 10 }}>{apiError}</div>
        )}

        {/* Slide to submit */}
        <SlideToSubmit
          onConfirm={handleConfirm}
          disabled={submitting}
          loading={submitting}
          label="Slide to add attendance"
          confirmedLabel="Attendance Added!"
        />

      </div>
    </div>
  );
};

export default PhotoPreview;
